/**
 * Whether the user is currently typing in a form control. Canvas-level
 * keyboard shortcuts (delete, arrows, space-to-pan, the `?` overlay, …) must
 * bail when this is true so they never hijack a keystroke meant for an input
 * — including the node title field.
 *
 * @returns True when the active element is an input/textarea/select or any
 *   contenteditable element.
 */
export function isEditingText(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  // Fallback to the attribute (jsdom doesn't compute `isContentEditable`).
  const editable = el.getAttribute('contenteditable');
  return editable === '' || editable === 'true';
}

/**
 * Blur the focused editable element, if any.
 *
 * Canvas pointer handlers call `event.preventDefault()` on mousedown, which
 * suppresses the browser's implicit blur — so an input the user was editing
 * (e.g. the node title) keeps focus and swallows canvas keyboard shortcuts
 * (space-to-pan, Delete, arrows). Call this on canvas pointer-down to restore
 * the expected "click the canvas → leave the field" behaviour.
 */
export function blurActiveEditable(): void {
  if (isEditingText()) {
    (document.activeElement as HTMLElement | null)?.blur();
  }
}
